import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts, glyphs, useMediaQuery } from "../../../Styles";
import { Button, CheckBox, CheckStyles, SECTION_BASIC, NewModal, MainContentPaddingTypes, NewInput, NewSelect} from "../..";
import { useAppSelector } from "../../../Store";
import { Graph_QL } from "../../../Services";
import { useSection } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useSections";
import { useParams } from "react-router";
import { SectionBasicTypes } from "../Section/sectionBasic";
import { NewFieldStyles } from "../../../Definitions";
import { LfSectionsInfo } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLfStructureCall";
import { useSelectOptions } from "../../_Components";

interface StartSection_Props {
  isModalOpen: boolean;
  onClose: () => void;
  system: number
}

export default function StartSectionModal({isModalOpen, onClose, system}: StartSection_Props) {
  const { t } = useTranslation();
  const [activePenId, setActivePenId] = useState<number[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [staticFeeding, setStaticFeeding] = useState(false);
  const [staticFeedAmount, setStaticFeedAmount] = useState<number>(0);
  const [message, setMessage] = useState<string>("")
  const [isSucces, setIsSuccess] = useState<boolean>()
  const { systemId: urlSystemId } = useParams(); // TODO routing changes in order to get a correct system info
  const systemId = urlSystemId ? +urlSystemId : system;
  const mobile = useMediaQuery(`(max-width: 650px)`);
  const {sectionOptions} = useSelectOptions({ systemId: systemId })

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);

  const { call: getLfSectionsInfo, data: lfSectionsState, startFeedingByLocation, feedLocation } = useSection(connection);
  const sections = useMemo( () => (lfSectionsState[domain] ? lfSectionsState[domain].data.filter(section => section.system_id === systemId) : undefined), [domain, lfSectionsState, systemId] );
  const [activeSection, setActiveSection] = useState< LfSectionsInfo | undefined >(sections !== undefined ? sections[0] : undefined);
  const numberOfPens = activeSection?.section_locations.length;

  const activePensList = useMemo(() => {
    let viewList: JSX.Element[] = [];
    
    activePenId.forEach((penId) => {
      let viewElement: JSX.Element[] = [];

      // Pen Name:
      let PenName = sections?.flatMap(section => section.section_locations).find(location => location.location_id === penId)?.name || penId;
      viewElement.push(<p key={'name_'+penId} style={{ gridArea: "1/1/2/2", height: "15px", ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary}}>{`${t("locationFeederPages.locationFeeder.pen", { defaultValue: "Pen" })} ${PenName}`}</p>);

      // Amount:
      let amount = "AMOUNT G"
      viewElement.push(<p key={'amount_'+penId} style={{ gridArea: "1/2/2/3", height: "15px", ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, filter: staticFeeding ? "opacity(0.3)" : "opacity(1)", textDecoration: staticFeeding ? "line-through" : "none"}}>{`${amount}`}</p>);

      // Time:
      let time = "TIME"
      viewElement.push(<p key={'time_' + penId} style={{ gridArea: "1/3/2/4", height: "15px", ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, filter: staticFeeding ? "opacity(0.3)" : "opacity(1)", textDecoration: staticFeeding ? "line-through" : "none" }}>{`${time}`}</p>);
      
      // Message:
      if (message !== "") {
        let isGood = !message.includes(penId + ',');
        viewElement.push(<div key={'res_' + penId} style={{ gridArea: "1/4/2/5", height: "15px", display: "flex", alignItems: "center", justifyContent: "center"}}>{isGood ? <glyphs.Check fillColor={colors.systemGreen} size={"15px"}/> : <glyphs.Empty fillColor={colors.systemRed} size={"15px"}/>}</div>);
      }

      viewList.push(
        <div style={{ display: "grid", gridTemplateColumns: "90px 1fr 1fr 15px", gridTemplateRows: "1fr" }}>
          {viewElement}
        </div>
      );
    })

    return viewList;
    }, [activePenId, message, sections, staticFeeding, t])

  useEffect(() => {
    getLfSectionsInfo({ ids: [systemId] }, connection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setActiveSection(sections !== undefined ? sections[0] : undefined);
  }, [sections]);

  const allPenIds = activeSection&&activeSection.section_locations.map(
    (pen) => pen.location_id
  );

  useEffect(() => {
    if (isModalOpen && activeSection) {
      setActivePenId(allPenIds ?? []);
      setIsAllSelected(true);
      setStaticFeeding(false);
    }
  }, [activeSection, isModalOpen]);

  useEffect(() => {
    setIsAllSelected(activePenId.length === numberOfPens);
  }, [activePenId, numberOfPens]);

  function handlePenSelected(penId: number) {
    setActivePenId((prev) => {
      if (!prev) return [penId];
      return prev.includes(penId)
        ? prev.filter((id) => id !== penId)
        : [...prev, penId];
    });
  }

  if (!sections) return null;
  const currentIndex = sections.findIndex(
    (s) => s.section_id === activeSection?.section_id
  );

  const handleStaticFeeding = async () => {
    if (activePenId) {
      await Promise.all(
        activePenId.map((async (location, index) => { 
          let Result = await feedLocation(location, true, staticFeedAmount);
          return {err: Result, location}
        }))
      ).then((e) => {
        const success = !e.some(res => res.err === false);
        if (success === true) {
          setMessage(t("success", {defaultValue: "Success"}));
          setIsSuccess(true)
        } else {
          let message = t("systemControls.general.feedingFailed", { defaultValue: "Feeding failed" });
          message += ' : ';
          console.log(e);
          e.forEach(res => res.err===false ? message += res.location + ', ' : null);
          message.trimEnd();
          setMessage(message)
          setIsSuccess(false)
        }
        setTimeout(() => {
          setIsSuccess(undefined)
          setMessage("");
        }, 5000);
      })
    }
  };

  const handleFeeding = async () => {
    if (activePenId) {
      await Promise.all(
        activePenId.map((async (location, index) => { 
          return await startFeedingByLocation(activePenId);
        }))
      ).then((e) => {
        const success = !e.some(res => res === false);
        if (success === true) {
          setMessage(t("success", {defaultValue: "Success"}));
          setIsSuccess(true)
        } else {
          setMessage(t("systemControls.general.feedingFailed", { defaultValue: "Feeding failed" }))
          setIsSuccess(false)
        }
        setTimeout(() => {
          setIsSuccess(undefined)
          setMessage("");
        }, 5000);
      })
    }
  }

  const handleOnClick = () => {
    if (staticFeeding) {
      handleStaticFeeding();
    } else {
      handleFeeding();
    }
  };

  return (
    activeSection && (
      <NewModal
        header={t("systemControls.general.systemControls", {
          defaultValue: "System Controls",
        })}
        modalState={isModalOpen}
        predefinedButtons={{
          onClose: () => { setActivePenId(allPenIds ?? []); setActiveSection(sections[0]); onClose() },
          onNext: () => {setActiveSection(sections[(currentIndex + 1) % sections.length])},
          onPrev: () => {setActiveSection(sections[(currentIndex - 1 + sections.length) % sections.length])},
        }}
        subHeader={activeSection?.name}
        overrides={{
          backgroundColor: colors.greyLightTertiary,
          mainContentPadding: MainContentPaddingTypes.primary,
        }}
        headerContent={
          <NewSelect
            options={sectionOptions ?? []}
            key={activeSection.section_id}
            onChange={(i) => setActiveSection(sections.find(section => section.section_id === i?.id))}
            valueOverrides={{
              defaultValue: sectionOptions?.find(option => option.id === activeSection.section_id)
            }}
            fieldOverrides={{
              fieldWidth: mobile ? "100px" : "160px"
            }}
          />
        }
        mainContent={
          <div
            style={{ width: mobile ? "300px" : "600px", boxSizing: "border-box", gap: "20px", display: "grid", gridTemplateColumns: mobile ? "1fr" : "270px 1fr", gridTemplateRows: mobile ? "fit-content(100%) fit-content(100%)" : "1fr", margin: "0 auto"}}>
            {/* Controls */}
            <div
              style={{ gridArea: mobile ? "2/1/3/2" : "1/1/2/2", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "start", gap: "15px"}}>
              <Button
                content={t("systemControls.general.startSelected", { defaultValue: "Start Selected" })}
                startEnhancer={glyphs.Start({ size: "30px" })}
                overrides={{
                  baseButton: { backgroundColor: colors.greySecondary },
                  hoverStyles: { backgroundColor: colors.greyDarkSecondary },
                  startEnhancerStyles: { ...css.button_PositionStartEnhancerStart},
                }}
                onClick={handleOnClick}
              />
              {/* {message} */}
              <div
                style={{ width: "100%", height: "30px", display: "flex", alignItems: "center", gap: "20px"}}>
                <CheckBox
                  checkStyle={CheckStyles.grey}
                  onChange={() => setStaticFeeding(!staticFeeding)}
                  isChecked={staticFeeding}
                  text={t("systemControls.general.staticFeeding", { defaultValue: "Static Feeding" })}
                />
                  {staticFeeding ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "5px"}}>
                    <NewInput
                      placeholder={t("units.g", { defaultValue: "g" })}
                      onChange={(e) => setStaticFeedAmount(Number(e))}
                      fieldOverrides={{
                        fieldStyle: NewFieldStyles.greyTertiary,
                        fieldWidth: "100px",
                      }}
                      valueOverrides={{ valueType: "number"}}
                    />
                    <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary}}> {t("units.g", { defaultValue: "G" })} </p>
                  </div>
                ) : (
                  <></>
                )}
              </div>
              <div style={{ height: "2px", width: "100%", backgroundColor: colors.greyTertiary}}/>
              <div style={{ width: "100%", maxHeight: "200px", overflowX: "hidden", overflowY: "scroll", scrollbarWidth: "thin", scrollbarColor: colors.greyTertiary + colors.transparent, display: "flex", flexDirection: "column", gap: "5px"}}>
                {activePenId.length === 0 ? (
                  <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, width: "100%", textAlign: "center" }}> {`${t("systemControls.general.noPensSelected", { defaultValue: "No pens selected" })}!`} </p>
                ) : (
                  <p style={{ ...fonts.header, ...fonts.fontSize4, color: colors.greyDarkPrimary, width: "100%" }}>{`${t("systemControls.general.feeding", {defaultValue: "feeding"})}:`}</p>
                )}
                {/* {activePenId.map((penId) => ( */}
                  {/* <div style={{ display: "grid", gridTemplateColumns: "90px 1fr 1fr", gridTemplateRows: "1fr"}}> */}
                    {/* <p key={penId} style={{ gridArea: "1/1/2/2", ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary}}>{`${t("locationFeederPages.locationFeeder.pen", {defaultValue: "Pen"})} ${sections?.flatMap(section => section.section_locations).find(location => location.location_id === penId)?.name || penId}`}</p> */}
                    {/* <p style={{ gridArea: "1/2/2/3", ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, filter: staticFeeding ? "opacity(0.3)" : "opacity(1)", textDecoration: staticFeeding ? "line-through" : "none" }}> ANTAL G TODO</p>  */}
                    {/* <p style={{ gridArea: "1/3/2/4", ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, filter: staticFeeding ? "opacity(0.3)" : "opacity(1)", textDecoration: staticFeeding ? "line-through" : "none"}}> TIDSPUNKT TODO</p> */}
                  {/* </div> */}
                {/* ))} */}
                {activePensList}
              </div>
                {/* <div style={{height: "30px", display: "flex", alignItems: "center", justifyContent: "center", width: "100%"}}>
                  <p style={{...fonts.paragraph, ...fonts.fontSize4, color: isSucces ? colors.systemGreen : colors.systemRed}}>{message}</p>
                </div> */}
            </div>

            {/* Pens */}
            <div style={{ gridArea: mobile ? "1/1/2/2" : "1/2/2/3", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "start", gap: "5px", borderBottom: mobile ? "2px solid " + colors.greyTertiary : "none", borderLeft: mobile? "none" : "2px solid " + colors.greyTertiary, paddingBottom: mobile ? "15px" : "0px"}}>
              <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary}}>
                {`${t("systemControls.general.clickPen", {defaultValue: "Click every pen you want to select"})}:`}
              </p>
              <div key={activeSection?.section_id} style={{ cursor: "pointer", transition: "filter 0.3s", width: "fit-content", height: "fit-content", position: "relative"}}>
                <SECTION_BASIC
                  onPenClick={handlePenSelected}
                  sectionId={activeSection?.section_id}
                  sectionType={SectionBasicTypes.selectablePen}
                  pens={activeSection?.section_locations}
                  selectedPenIds={activePenId}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "5px"}}>
                <p style={{ ...fonts.paragraph, ...fonts.fontSize4,  color: colors.greyDarkPrimary }}>
                  {`${t("systemControls.general.orSelectAll", { defaultValue: "Or select all" })}:`}
                </p>
                <CheckBox
                  checkStyle={CheckStyles.grey}
                  onChange={() => {
                    if (isAllSelected) {
                      setActivePenId([]);
                    } else {
                      setActivePenId(allPenIds ?? []);
                    }
                    setIsAllSelected(!isAllSelected);
                  }}
                  isChecked={isAllSelected}
                />
              </div>
            </div>
          </div>
        }
      />
    )
  );
}
